import {
  type MyReview,
  REVIEW_CONTENT_MAX,
  REVIEW_CONTENT_MIN,
  REVIEW_IMAGES_MAX,
} from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { reviewErrorMessage } from '@/entities/review';
import { Button, cn, FormSection, ImageUploadPicker } from '@/shared/ui';
import type { ReviewFormValues } from '../model/review-form-values';
import { useReviewImageUploads } from '../model/use-review-image-uploads';
import { StarRatingInput } from './star-rating-input';

interface ReviewFormProps {
  readonly initial?: MyReview;
  readonly onSubmit: (values: ReviewFormValues) => Promise<void>;
  readonly submitLabel: string;
}

export const ReviewForm = ({ initial, onSubmit, submitLabel }: ReviewFormProps): ReactElement => {
  const [rating, setRating] = useState(initial?.rating ?? 0);
  const [content, setContent] = useState(initial?.content ?? '');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const uploads = useReviewImageUploads(initial?.images ?? []);
  const length = Array.from(content.trim()).length;
  const valid = rating >= 1 && length >= REVIEW_CONTENT_MIN && length <= REVIEW_CONTENT_MAX;
  const rejectionDetail =
    uploads.rejections === null
      ? ''
      : [
          uploads.rejections.type > 0 && `형식 ${uploads.rejections.type}장`,
          uploads.rejections.size > 0 && `용량 ${uploads.rejections.size}장`,
          uploads.rejections.limit > 0 && `장수 ${uploads.rejections.limit}장`,
        ]
          .filter(Boolean)
          .join(', ');
  const blocked = uploads.isBusy || uploads.hasFailed;

  return (
    <form
      className="flex flex-col gap-12"
      onSubmit={async (event) => {
        event.preventDefault();
        if (!valid || blocked || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
          await onSubmit({ rating, content: content.trim(), imageIds: uploads.readyIds });
        } catch (caught) {
          setError(reviewErrorMessage(caught));
        } finally {
          setSubmitting(false);
        }
      }}
    >
      <FormSection order={1} title="만족도">
        <StarRatingInput value={rating} onChange={setRating} />
      </FormSection>
      <FormSection order={2} title="후기 내용">
        <div className="flex flex-col gap-2">
          <textarea
            aria-label="후기 내용"
            value={content}
            maxLength={REVIEW_CONTENT_MAX * 2}
            onChange={(event) => setContent(event.target.value)}
            rows={8}
            placeholder="인쇄 품질, 색감, 마감, 받아 본 분들의 반응 등 제품을 써 보신 경험을 자유롭게 남겨 주세요."
            className="min-h-48 w-full resize-y rounded-2xl border border-input bg-background px-5 py-4 text-[15px] leading-relaxed transition-colors placeholder:text-muted-foreground focus-visible:border-foreground focus-visible:outline-none"
          />
          <div className="flex items-center justify-between gap-4 text-xs text-muted-foreground">
            <span>{REVIEW_CONTENT_MIN}자 이상 써 주세요</span>
            <span
              className={cn(
                'tabular-nums',
                length > 0 && length < REVIEW_CONTENT_MIN && 'text-destructive',
              )}
            >
              {length.toLocaleString('ko-KR')} / {REVIEW_CONTENT_MAX.toLocaleString('ko-KR')}
            </span>
          </div>
        </div>
      </FormSection>
      <FormSection order={3} title="사진 (선택)">
        <div className="flex flex-col gap-3">
          <ImageUploadPicker
            items={uploads.items}
            max={REVIEW_IMAGES_MAX}
            onAdd={uploads.add}
            onRemove={uploads.remove}
            onRetry={uploads.retry}
          />
          {uploads.rejections !== null && (
            <p role="status" className="text-sm text-muted-foreground">
              JPG·PNG·WEBP, 장당 10MB 이하, 최대 {REVIEW_IMAGES_MAX}장까지 올릴 수 있어요. 일부
              사진은 추가하지 않았어요. ({rejectionDetail})
            </p>
          )}
          {uploads.hasFailed && (
            <p role="alert" className="text-sm text-destructive">
              올리지 못한 사진이 있어요. 다시 시도하거나 빼 주세요.
            </p>
          )}
        </div>
      </FormSection>
      {error !== null && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="flex flex-col gap-3 border-t border-foreground/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          등록한 후기는 상품 페이지에 바로 공개되고, 이름은 첫 글자만 보여요.
        </p>
        <Button
          type="submit"
          className="h-12 w-full px-10 text-[15px] sm:w-auto"
          disabled={!valid || blocked || submitting}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};
